import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';
import { toast } from '../components/ui/Toast';

// ── DTOs ──

interface OtItemDTO {
  id: string;
  proforma_item_id: string;
  codigo: string;
  descripcion: string;
  descripcion_proyecto: string | null;
  categoria: string;
  unidad: string;
  cantidad: number;
  notas: string | null;
}

interface OtMateriaPrimaDTO {
  id: string;
  ot_item_id: string;
  material_id: string;
  material_nombre: string;
  material_sku: string | null;
  material_unidad: string;
  cantidad_total: number;
}

interface OtMaestroDTO {
  id: string;
  nombre: string;
  especialidades: string[];
}

export interface OtDetailDTO {
  id: string;
  project_id: string;
  proforma_id: string;
  estado: string;
  fecha_inicio: string | null;
  fecha_fin: string | null;
  items: OtItemDTO[];
  materia_prima: OtMateriaPrimaDTO[];
  equipo_trabajo: {
    residente: { id: string; nombre: string } | null;
    maestros: OtMaestroDTO[];
  };
  created_at: string;
  updated_at: string;
}

// ── Get OT for project ──
export function useOrdenTrabajo(projectId: string) {
  return useQuery({
    queryKey: queryKeys.ordenesTrabajo.detail(projectId),
    queryFn: async () => {
      const response = await api.get<{ data: OtDetailDTO }>(`/projects/${projectId}/ot`);
      return response.data.data;
    },
    enabled: !!projectId,
    retry: false,
  });
}

// ── Generate OT PDF ──
export function useGenerateOtPdf() {
  return useMutation({
    mutationFn: async (projectId: string) => {
      const response = await api.post(`/projects/${projectId}/ot/pdf`, {}, { responseType: 'blob' });
      // Check if HTML (local dev) or PDF (Lambda)
      const contentType = response.headers['content-type'] ?? '';
      if (contentType.includes('text/html')) {
        // Local dev: open HTML in new tab
        const html = await (response.data as Blob).text();
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        return;
      }
      // Lambda: download PDF
      const url = URL.createObjectURL(response.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orden-trabajo.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    },
    onSuccess: () => {
      toast('success', 'PDF generado exitosamente');
    },
  });
}

// ── Generate OT XLSX ──
export function useGenerateOtXlsx() {
  return useMutation({
    mutationFn: async (projectId: string) => {
      const response = await api.post<{ data: { xlsx: string; filename: string } }>(`/projects/${projectId}/ot/xlsx`);
      const { xlsx, filename } = response.data.data;
      const byteChars = atob(xlsx);
      const byteArray = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteArray[i] = byteChars.charCodeAt(i);
      }
      const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    },
    onSuccess: () => {
      toast('success', 'Excel generado exitosamente');
    },
  });
}

// ── Create OT ──
export function useCreateOt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (projectId: string) => {
      const response = await api.post<{ data: { id: string; ot_items_count: number; materia_prima_count: number } }>(
        `/projects/${projectId}/ot`,
      );
      return response.data.data;
    },
    onSuccess: (_data, projectId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ordenesTrabajo.detail(projectId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(projectId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.projectSummary.detail(projectId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.summary });
      toast('success', `OT creada: ${_data.ot_items_count} items, ${_data.materia_prima_count} materiales`);
    },
  });
}
